/**
 * LakePalaceDestination — data resolution.
 *
 * Turns InvitationData into the plain view-model the five beats render.
 * Rules:
 *  - Explicit customData.wedding fields always win; the event-name split is a
 *    fallback only (build-template-from-manifest skill forbids inventing names).
 *  - Every unresolved value stays an honest placeholder ("To be announced",
 *    "shared by the host") — nothing is invented.
 */

import type { InvitationData } from '@/src/modules/templates/types';
import type { WeddingEventItem } from '@/src/types';
import {
  extractCoupleNames,
  parseFlexibleDate,
} from '@/src/infrastructure/lib/weddingDataHelper';
import { safeLinkUrl } from '@/src/modules/renderer/url-safety';

export interface CoupleNames {
  /** Two display lines when a couple is resolvable, otherwise a single line. */
  lines: string[];
  /** Names joined for aria-label / social copy. */
  joined: string;
  /** True when lines[0] / lines[1] are groom / bride (gold separator shown). */
  isCouple: boolean;
}

export interface DateBlock {
  /** "14" when a real date exists. */
  day: string;
  /** "FEB 2027" — month + year line. */
  monthYear: string;
  /** "Sunday" weekday line. */
  weekday: string;
  /** "7:00 PM" or '' when unknown. */
  time: string;
  /** Full long form for aria / compact lines. */
  long: string;
  hasDate: boolean;
}

export interface FunctionSlot {
  key: string;
  title: string;
  dateLabel: string;
  timeLabel: string;
  venueLabel: string;
  mapsUrl: string | null;
  confirmed: boolean;
}

export interface LakePalaceContent {
  names: CoupleNames;
  hostLine: string;
  message: string;
  date: DateBlock;
  venue: string;
  mapsUrl: string | null;
  functions: FunctionSlot[];
  rsvpUrl: string | null;
  travelNote: string;
}

const FALLBACK_DATE: DateBlock = {
  day: '',
  monthYear: '',
  weekday: '',
  time: '',
  long: 'Date to be announced',
  hasDate: false,
};

function safeFormat(input: string | Date | null | undefined, opts: Intl.DateTimeFormatOptions, fallback: string): string {
  const d = parseFlexibleDate(input ?? null);
  if (!d) return fallback;
  try {
    return new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', ...opts }).format(d);
  } catch {
    try {
      return new Intl.DateTimeFormat('en-IN', opts).format(d);
    } catch {
      return fallback;
    }
  }
}

export function resolveCoupleNames(data: InvitationData): CoupleNames {
  const wedding = data.customData?.wedding;
  const groom = wedding?.groom?.fullName || wedding?.groom?.nickname || '';
  const bride = wedding?.bride?.fullName || wedding?.bride?.nickname || '';
  if (groom && bride) {
    return { lines: [groom, bride], joined: `${groom} & ${bride}`, isCouple: true };
  }

  const extracted = extractCoupleNames(data.eventName);
  if (extracted?.groom && extracted?.bride) {
    return {
      lines: [extracted.groom, extracted.bride],
      joined: `${extracted.groom} & ${extracted.bride}`,
      isCouple: true,
    };
  }

  const single = (groom || bride || data.eventName || '').trim();
  if (single) {
    return { lines: [single], joined: single, isCouple: false };
  }
  return { lines: ['The Couple'], joined: 'The Couple', isCouple: false };
}

export function resolveDate(data: InvitationData): DateBlock {
  const d = parseFlexibleDate(data.eventDate);
  if (!d) return { ...FALLBACK_DATE, time: data.eventTime?.trim() || '' };
  try {
    return {
      day: safeFormat(d, { day: 'numeric' }, ''),
      monthYear: `${safeFormat(d, { month: 'short' }, '').toUpperCase()} ${safeFormat(d, { year: 'numeric' }, '')}`.trim(),
      weekday: safeFormat(d, { weekday: 'long' }, ''),
      time: data.eventTime?.trim() || '',
      long: safeFormat(d, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }, 'Date to be announced'),
      hasDate: true,
    };
  } catch {
    return { ...FALLBACK_DATE };
  }
}

function dayLabel(date: string | undefined, fallback: string): string {
  const d = parseFlexibleDate(date ?? null);
  if (!d) return fallback;
  return safeFormat(d, { weekday: 'short', day: 'numeric', month: 'short' }, fallback);
}

function timeLabel(event: WeddingEventItem, mainTime?: string): string {
  const raw = event.startTime || event.endTime || mainTime || '';
  const d = parseFlexibleDate(raw || null);
  if (!d) return raw.trim();
  return safeFormat(d, { hour: 'numeric', minute: '2-digit', hour12: true }, raw.trim());
}

function mapsQueryUrl(venue: string | null | undefined): string | null {
  const venueText = (venue ?? '').trim();
  if (!venueText) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venueText)}`;
}

interface SlotSpec {
  key: string;
  title: string;
  re: RegExp;
}

const SLOT_SPECS: SlotSpec[] = [
  { key: 'mehndi', title: 'Mehndi', re: /mehndi|mehendi|henna|mangalsutra|hal(di|ad)/i },
  { key: 'sangeet', title: 'Sangeet', re: /sangeet|sangam|jaggo|garba|music night/i },
  { key: 'ceremony', title: 'Ceremony', re: /ceremony|pheras|vivah|vivaah|wedding|mangal|nikah|kuldevi/i },
  { key: 'reception', title: 'Reception', re: /reception|party|dinner|after ?party/i },
];

export function resolveFunctions(data: InvitationData): FunctionSlot[] {
  const events = (data.customData?.wedding?.weddingEvents ?? []).filter(
    (e) => e && e.visible !== false && (e.title ?? '').trim().length > 0,
  );
  const claimed = new Set<number>();

  const plan = SLOT_SPECS.map((spec) => {
    const idx = events.findIndex((e, i) => !claimed.has(i) && spec.re.test(e.title ?? ''));
    if (idx >= 0) {
      claimed.add(idx);
      return { spec, event: events[idx] };
    }
    return { spec, event: undefined as WeddingEventItem | undefined };
  });

  const matchedCount = plan.filter((p) => p.event).length;
  const unused = events.filter((_, i) => !claimed.has(i));

  if (matchedCount === 0 && unused.length > 0) {
    // Real events exist but none match the house titles: surface the host's own
    // titles rather than mislabeling what they actually planned.
    return plan.map((p, i) => buildSlot(p.spec, unused[i], data));
  }

  // Fill the remaining house slots from any unmatched events. The arcade is a
  // fixed 2×2 grid (four slots), so events beyond the fourth are intentionally
  // not rendered rather than overflowing the layout.
  let next = 0;
  return plan.map((p) => buildSlot(p.spec, p.event ?? unused[next++], data));
}

function buildSlot(spec: SlotSpec, event: WeddingEventItem | undefined, data: InvitationData): FunctionSlot {
  const isCeremony = spec.key === 'ceremony';
  const dateSource = event?.date ?? (isCeremony ? data.eventDate : undefined);
  const date = dayLabel(dateSource, '');
  const venue = (event?.venue ?? '').trim() || (isCeremony ? (data.eventVenue ?? '').trim() : '');
  return {
    key: spec.key,
    title: (event?.title ?? spec.title).trim(),
    dateLabel: date || 'To be announced',
    timeLabel: event ? timeLabel(event, data.eventTime) : isCeremony ? (data.eventTime?.trim() || '') : '',
    venueLabel: venue,
    mapsUrl: event?.mapsLink ? safeLinkUrl(event.mapsLink) : mapsQueryUrl(venue),
    confirmed: Boolean(date) || Boolean(event),
  };
}

export function resolveContent(data: InvitationData): LakePalaceContent {
  const names = resolveCoupleNames(data);
  const venue = (data.eventVenue ?? '').trim();
  const travelRaw = data.customData?.travelNotes;
  const travelNote =
    typeof travelRaw === 'string' && travelRaw.trim()
      ? travelRaw.trim()
      : 'Travel and stay details will be shared by the host.';

  return {
    names,
    hostLine: (data.hostName ?? '').trim(),
    message: (data.customMessage ?? '').trim(),
    date: resolveDate(data),
    venue: venue || 'Venue to be announced',
    mapsUrl: venue ? mapsQueryUrl(venue) : null,
    functions: resolveFunctions(data),
    rsvpUrl: safeLinkUrl(data.rsvpLink),
    travelNote,
  };
}
